/**
 * The two narrow places where an app value and a generated database type cannot
 * agree, named so they are greppable instead of scattered as bare casts.
 *
 * Both exist because `supabase gen types` cannot express something Postgres is
 * doing. Neither is a licence to silence a real mismatch: if `pnpm typecheck`
 * complains about something that is NOT one of these two shapes, the code and
 * the schema genuinely disagree and the code is what should change.
 *
 * @module services/dbBoundary
 */

import type { Json } from '@/types/database.types';

/**
 * Pass a deliberately NULL argument to a Postgres function.
 *
 * **Postgres function parameters are always nullable** — there is no `NOT NULL`
 * on a parameter — but the generator emits them as non-null, so a call that
 * means "no workspace, i.e. the global default" does not type-check. This names
 * that intent.
 *
 * Used by `setCreditRate(workspaceId: string | null)`, where NULL is the global
 * rate (migration 053 keys the per-workspace row with
 * `ON CONFLICT (workspace_id) WHERE workspace_id IS NOT NULL`), and by the
 * coverage RPCs, where a NULL bound means "unbounded".
 *
 * NOT for a column: a NOT NULL column that receives NULL is a real defect and
 * must be fixed with a guard, not with this.
 */
export const nullableArg = <T>(value: T | null | undefined): T => value as T;

/**
 * Hand a plain, JSON-serialisable app value to a `jsonb` column.
 *
 * `Json` is a recursive union, and a named interface is not assignable to it
 * even when every field would serialise — TypeScript compares the declared
 * shape, not what `JSON.stringify` would produce. Marking the boundary is the
 * conventional answer; widening every interface to `Json` would infect types
 * that have nothing to do with the database.
 *
 * The caller owes one thing: the value must actually be serialisable. No
 * `Date`, no `Map`, no `undefined` inside an array, no cycles.
 */
export const toJson = <T>(value: T): Json => value as unknown as Json;
